import React from 'react'
import { useCart } from '../../context/CartContext'
import { useAuth } from '../../hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { buildLoginUrl } from '../../utils/returnUrl'

export function CartDrawer() {
  const { cart, isOpen, closeCart, removeItem, updateQty } = useCart()
  const { user } = useAuth()
  const navigate = useNavigate()

  // Checkout is the one step that needs an account. A guest is sent to sign in
  // with /checkout as the returnUrl; their bag is merged into the real cart on
  // the way back (CartContext).
  function goToCheckout() {
    closeCart()
    navigate(user ? '/checkout' : buildLoginUrl('/checkout'))
  }

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div onClick={closeCart} style={s.backdrop} />
      )}

      {/* Drawer */}
      <div style={{
        ...s.drawer,
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)'
      }}>
        {/* Header */}
        <div style={s.header}>
          <div>
            <h2 style={s.title}>Your Cart</h2>
            <p style={s.count}>{cart.item_count} {cart.item_count === 1 ? 'item' : 'items'}</p>
          </div>
          <button onClick={closeCart} style={s.closeBtn}>✕</button>
        </div>

        {/* Items */}
        <div style={s.items}>
          {cart.items.length === 0 ? (
            <div style={s.empty}>
              <div style={s.emptyIcon}>✦</div>
              <p style={s.emptyText}>Your cart is empty</p>
              <p style={s.emptySubtext}>Discover our collection</p>
              <button onClick={() => { closeCart(); navigate('/shop') }} style={s.shopBtn}>
                Browse Shop
              </button>
            </div>
          ) : (
            cart.items.map(item => (
              <div key={item.id} style={s.item}>
                {/* Mood indicator */}
                <div style={{
                  ...s.itemMood,
                  background: getMoodColor(item.mood)
                }} />

                <div style={s.itemInfo}>
                  <p style={s.itemName}>{item.product_name}</p>
                  <p style={s.itemPrice}>
                    {/* The "was" price only renders when the server sent one —
                        it never invents a saving out of a missing field. */}
                    {item.compare_at_price != null && (
                      <span style={s.itemWas}>₹{item.compare_at_price.toLocaleString('en-IN')}</span>
                    )}
                    ₹{item.price_at_add.toLocaleString('en-IN')}
                  </p>
                </div>

                <div style={s.itemControls}>
                  <div style={s.qtyRow}>
                    <button onClick={() => item.quantity > 1
                      ? updateQty(item.id, item.quantity - 1)
                      : removeItem(item.id)
                    } style={s.qtyBtn}>−</button>
                    <span style={s.qty}>{item.quantity}</span>
                    <button onClick={() => updateQty(item.id, item.quantity + 1)}
                      style={s.qtyBtn}>+</button>
                  </div>
                  <button onClick={() => removeItem(item.id)} style={s.removeBtn}>Remove</button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {cart.items.length > 0 && (
          <div style={s.footer}>
            <div style={s.totals}>
              <div style={s.totalRow}>
                <span style={s.totalLabel}>Subtotal</span>
                <span style={s.totalValue}>₹{cart.total.toLocaleString('en-IN')}</span>
              </div>
              {/* Listed prices already include GST — show the component the
                  server extracted, rather than adding anything on top. */}
              <div style={s.gstNote}>
                <span>incl. GST (18%)</span>
                <span>₹{cart.tax.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
              </div>
              <div style={{ ...s.totalRow, borderTop: '1px solid rgba(232,220,192,0.2)', paddingTop: '12px' }}>
                <span style={{ ...s.totalLabel, color: '#e8e8f0', fontWeight: 700 }}>Total</span>
                <span style={{ ...s.totalValue, color: '#e8dcc0', fontSize: '1.1rem', fontWeight: 700 }}>
                  ₹{cart.total.toLocaleString('en-IN')}
                </span>
              </div>
              <div style={s.shippingNote}>
                <span>Shipping</span>
                <span style={{ color: '#4ade80' }}>Free</span>
              </div>
            </div>
            <button onClick={goToCheckout} style={s.checkoutBtn}>
              Proceed to Checkout
            </button>
            {!user && (
              <p style={s.guestNote}>You'll be asked to sign in at checkout</p>
            )}
            <button onClick={() => { closeCart(); navigate('/shop') }} style={s.continueBtn}>
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </>
  )
}

function getMoodColor(mood?: string): string {
  const colors: Record<string, string> = {
    romantic:   'linear-gradient(135deg, #8B1A4A, #C4507A)',
    mysterious: 'linear-gradient(135deg, #2D1B69, #5B2D8E)',
    energetic:  'linear-gradient(135deg, #E85D00, #FF9A00)',
    calm:       'linear-gradient(135deg, #1A5276, #2E86AB)',
    fresh:      'linear-gradient(135deg, #0D6E4A, #2EC4B6)',
  }
  // Fallback: warm taupe from the landing's espresso family (not cool grey).
  return colors[mood || ''] || 'linear-gradient(135deg, #4a443c, #6a6156)'
}

const s: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    backdropFilter: 'blur(4px)', zIndex: 1100
  },
  drawer: {
    position: 'fixed', top: 0, right: 0, bottom: 0,
    width: '420px', maxWidth: '100vw',
    background: '#14100d',
    borderLeft: '1px solid rgba(232,220,192,0.15)',
    zIndex: 1200,
    display: 'flex', flexDirection: 'column',
    transition: 'transform 0.4s cubic-bezier(0.4,0,0.2,1)',
    fontFamily: "'Fira Code', monospace"
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '28px 24px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.06)'
  },
  title:  { margin: 0, fontSize: '1.3rem', fontWeight: 600, color: '#e8e8f0', fontFamily: "'Fira Code', monospace" },
  count:  { margin: '4px 0 0', fontSize: '0.8rem', color: '#8a8a9a' },
  closeBtn: {
    background: 'none', border: 'none', color: '#8a8a9a',
    cursor: 'pointer', fontSize: '1rem', padding: '4px'
  },
  items: { flex: 1, overflowY: 'auto', padding: '16px 24px' },
  empty: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', height: '300px', gap: '12px'
  },
  emptyIcon:    { fontSize: '2rem', color: 'rgba(232,220,192,0.4)' },
  emptyText:    { margin: 0, color: '#e8e8f0', fontSize: '1rem', fontWeight: 600 },
  emptySubtext: { margin: 0, color: '#8a8a9a', fontSize: '0.85rem' },
  shopBtn: {
    marginTop: '8px', background: 'none',
    border: '1px solid rgba(232,220,192,0.4)', borderRadius: '50px',
    padding: '10px 24px', color: '#e8dcc0', cursor: 'pointer', fontSize: '0.85rem'
  },
  item: {
    display: 'flex', gap: '12px', alignItems: 'flex-start',
    padding: '16px 0', borderBottom: '1px solid rgba(255,255,255,0.04)'
  },
  itemMood:  { width: '4px', borderRadius: '4px', alignSelf: 'stretch', flexShrink: 0 },
  itemInfo:  { flex: 1 },
  itemName:  { margin: 0, fontSize: '0.9rem', color: '#e8e8f0', fontWeight: 500 },
  itemPrice: { margin: '4px 0 0', fontSize: '0.85rem', color: '#e8dcc0' },
  // Quieter than the live price and struck through — it must never be mistaken
  // for the amount being charged.
  itemWas:   { color: 'rgba(255,255,255,0.35)', textDecoration: 'line-through', marginRight: '8px', fontSize: '0.78rem' },
  itemControls: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' },
  qtyRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  qtyBtn: {
    width: '24px', height: '24px', borderRadius: '50%',
    border: '1px solid rgba(255,255,255,0.15)',
    background: 'rgba(255,255,255,0.05)', color: '#e8e8f0',
    cursor: 'pointer', fontSize: '0.9rem', display: 'flex',
    alignItems: 'center', justifyContent: 'center'
  },
  qty:       { fontSize: '0.9rem', color: '#e8e8f0', minWidth: '20px', textAlign: 'center' },
  removeBtn: {
    background: 'none', border: 'none', color: '#8a8a9a',
    cursor: 'pointer', fontSize: '0.75rem', textDecoration: 'underline'
  },
  footer: {
    padding: '20px 24px 28px',
    borderTop: '1px solid rgba(255,255,255,0.06)'
  },
  totals:     { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' },
  totalRow:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  totalLabel: { fontSize: '0.85rem', color: '#8a8a9a' },
  totalValue: { fontSize: '0.85rem', color: '#e8e8f0' },
  // Informational only — GST is already inside the subtotal above.
  gstNote:      { display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'rgba(255,255,255,0.35)' },
  shippingNote: { display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginTop: '2px' },
  checkoutBtn: {
    width: '100%', padding: '14px',
    background: 'linear-gradient(180deg, #f7f4ea, #e6dfcd)',
    border: 'none', borderRadius: '50px',
    color: '#0f0c0a', fontWeight: 700, fontSize: '0.95rem',
    cursor: 'pointer', marginBottom: '10px'
  },
  guestNote: {
    margin: '0 0 10px', fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)',
    textAlign: 'center'
  },
  continueBtn: {
    width: '100%', padding: '12px',
    background: 'none', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '50px', color: '#8a8a9a', fontSize: '0.85rem', cursor: 'pointer'
  }
}